// Logo Siparis sayfasi. Liste + detayda baslik bilgileri ve satirlar (OrderLine).
// Logo'dan cekme odakli (GET /api/orders); detay salt-goruntu.
import { useCallback, useEffect, useMemo, useState } from 'react';
import DataGrid, { Column, Scrolling } from 'devextreme-react/data-grid';
import Splitter, { Item as SplitterItem } from 'devextreme-react/splitter';
import notify from 'devextreme/ui/notify';
import { CustomDataGrid } from '../components/CustomDataGrid';
import { Order } from '../types';
import { getOrders } from '../api/orders';
import { lt } from '../i18n';

const ORDER_STATUS: Record<number, string> = { 1: 'Oneri', 2: 'Sevkedilemez', 3: 'Sevkedilebilir', 4: 'Onayli' };

function fmtDate(s?: string) {
  if (!s) return '—';
  try { return new Date(s).toLocaleDateString('tr-TR'); } catch { return s; }
}

const columns = [
  { dataField: 'orderNumber', caption: 'Siparis No', width: 150 },
  { dataField: 'orderDate', caption: 'Tarih', width: 110, cellRender: (d: any) => fmtDate(d.data.orderDate) },
  { dataField: 'clientCode', caption: 'Cari Kodu', width: 130 },
  { dataField: 'clientName', caption: 'Cari Adi', width: 'auto' },
  { dataField: 'orderStatus', caption: 'Durum', width: 110,
    cellRender: (d: any) => ORDER_STATUS[d.data.orderStatus] ?? d.data.orderStatus ?? '—' },
  { dataField: 'totalNet', caption: 'Net Tutar', width: 130, alignment: 'right' as const, dataType: 'number' as const,
    format: '#,##0.00' },
];

export default function LogoOrdersPage() {
  const [rows, setRows] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Order | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await getOrders();
    setLoading(false);
    if (res.ok) setRows(res.data ?? []);
    else { notify(`${lt('loadError')}: ${res.message ?? ''}`, 'error', 4000); setRows([]); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const header = useMemo(() => {
    if (!selected) return null;
    const o = selected;
    const kv: Array<[string, string]> = [
      ['Siparis No', o.orderNumber ?? '—'],
      ['Tarih', fmtDate(o.orderDate)],
      ['Cari', `${o.clientCode ?? ''} ${o.clientName ?? ''}`.trim() || '—'],
      ['Satici', o.salesmanCode ?? '—'],
      ['Odeme Plani', o.paymentCode ?? '—'],
      ['Durum', ORDER_STATUS[o.orderStatus ?? -1] ?? String(o.orderStatus ?? '—')],
      ['Brut', String(o.totalGross ?? 0)],
      ['KDV', String(o.totalVat ?? 0)],
      ['Net', String(o.totalNet ?? 0)],
    ];
    return kv;
  }, [selected]);

  return (
    <div style={{ height: 'calc(100vh - 120px)' }}>
      <Splitter orientation="vertical" style={{ height: '100%' }}>
        <SplitterItem size="55%" minSize="120px" collapsible>
          <CustomDataGrid
            data={rows}
            columns={columns}
            keyExpr="id"
            height="100%"
            t={lt}
            selectionEnabled={false}
            filterRowVisible
            noDataText={loading ? '...' : lt('noData')}
            onRowClick={(data: Order) => setSelected(data)}
            onRefresh={load}
          />
        </SplitterItem>

        <SplitterItem size="45%" minSize="120px" collapsible>
          {selected ? (
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <div style={{ padding: '8px 12px', borderBottom: '1px solid #e5e7eb' }}>
                <strong style={{ fontSize: 14 }}>{selected.orderNumber || `#${selected.id}`}</strong>
                {selected.description && <div style={{ color: '#6b7280', fontSize: 12 }}>{selected.description}</div>}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 24px', padding: '8px 12px', fontSize: 12 }}>
                {header?.map(([k, v]) => (
                  <div key={k}><span style={{ color: '#9ca3af' }}>{k}: </span><span>{v}</span></div>
                ))}
              </div>
              <div style={{ flex: 1, minHeight: 0, padding: '0 12px 12px' }}>
                <DataGrid dataSource={selected.lines ?? []} keyExpr="lineNum" height="100%" showBorders columnAutoWidth>
                  <Scrolling mode="virtual" />
                  <Column dataField="lineNum" caption="#" width={50} />
                  <Column dataField="materialCode" caption="Malzeme" width={130} />
                  <Column dataField="materialName" caption="Adi" />
                  <Column dataField="quantity" caption="Miktar" width={90} dataType="number" />
                  <Column dataField="unitCode" caption="Birim" width={80} />
                  <Column dataField="price" caption="Fiyat" width={100} dataType="number" format="#,##0.00" />
                  <Column dataField="vatRate" caption="KDV %" width={80} dataType="number" />
                  <Column dataField="totalNet" caption="Net" width={110} dataType="number" format="#,##0.00" />
                </DataGrid>
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#9ca3af' }}>
              {lt('selectRecord')}
            </div>
          )}
        </SplitterItem>
      </Splitter>
    </div>
  );
}
